import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import BusinessIcon from "@mui/icons-material/Business";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { MenuTop } from "../../../../components/menu/top/menu-top";
import {
    AdminCrudAlerts,
    AdminCrudDeleteDialog,
    AdminCrudFormDialog,
    AdminCrudHeader,
    AdminCrudSearch
} from "../../../../components/admin-crud/admin-crud";
import {
    CompanyPayload,
    createSuperAdminCompany,
    deleteSuperAdminCompany,
    getSuperAdminCompaniesDashboard,
    updateSuperAdminCompany
} from "../../../../features/search/service/company-service";
import { getLanguagesSelect } from "../../../../features/search/service/languages-service";
import { Company } from "../../../../shared/types/company";
import { SelectOption } from "../../../../shared/types/select-options";
import { useTranslation } from "../../../../shared/i18n/i18n-context";
import { ProjectLoader } from "../../../../components/project-loader/project-loader";
import "./styles/companies-dashboard.css";

type CompanyFormState = {
    name: string;
    cnpj: string;
    description: string;
    licenses: string;
    isPlanActive: boolean;
    languageId: string;
};

const emptyForm: CompanyFormState = {
    name: "",
    cnpj: "",
    description: "",
    licenses: "0",
    isPlanActive: true,
    languageId: ""
};

export const CompaniesDashboard = () => {
    const { language, t } = useTranslation('admin');
    const { t: menuT } = useTranslation('menu');
    const [companies, setCompanies] = useState<Company[]>([]);
    const [languages, setLanguages] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [editingCompany, setEditingCompany] = useState<Company | null>(null);
    const [form, setForm] = useState<CompanyFormState>(emptyForm);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState("");

    const fetchCompanies = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const [companiesResponse, languagesResponse] = await Promise.all([
                getSuperAdminCompaniesDashboard(),
                getLanguagesSelect()
            ]);

            setCompanies(companiesResponse.data);
            setLanguages(languagesResponse);
        } catch (err: any) {
            setError(err.message || t('loadCompaniesError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    const filteredCompanies = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();

        if (!term) {
            return companies;
        }

        return companies.filter((company) => {
            return (
                company.name?.toLowerCase().includes(term) ||
                company.description?.toLowerCase().includes(term) ||
                company.cnpj?.toLowerCase().includes(term)
            );
        });
    }, [companies, searchTerm]);

    const totals = useMemo(() => {
        return companies.reduce(
            (acc, company) => {
                acc.licenses += Number(company.licenses) || 0;
                acc.used += Number(company.testsUsed) || 0;
                acc.active += company.isPlanActive ? 1 : 0;
                return acc;
            },
            { licenses: 0, used: 0, active: 0 }
        );
    }, [companies]);

    const openAddForm = () => {
        setEditingCompany(null);
        setForm(emptyForm);
        setFormOpen(true);
    };

    const openEditForm = (company: Company) => {
        setEditingCompany(company);
        setForm({
            name: company.name || "",
            cnpj: company.cnpj || "",
            description: company.description || "",
            licenses: String(company.licenses ?? 0),
            isPlanActive: Boolean(company.isPlanActive),
            languageId: company.language_id || company.activeLanguage?.id || ""
        });
        setFormOpen(true);
    };

    const closeForm = () => {
        if (saving) return;
        setFormOpen(false);
        setEditingCompany(null);
        setForm(emptyForm);
    };

    const updateForm = (field: keyof CompanyFormState, value: string | boolean) => {
        setForm((current) => ({ ...current, [field]: value }));
    };

    const buildPayload = (): CompanyPayload => ({
        name: form.name.trim(),
        cnpj: form.cnpj.trim() || null,
        description: form.description.trim(),
        licenses: Number(form.licenses),
        isPlanActive: form.isPlanActive,
        languageId: form.languageId || null
    });

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setError(t('companyNameRequired'));
            return;
        }

        const licenses = Number(form.licenses);
        if (!Number.isInteger(licenses) || licenses < 0) {
            setError(t('licensesInvalid'));
            return;
        }

        try {
            setSaving(true);
            setError("");

            if (editingCompany?.id) {
                await updateSuperAdminCompany(editingCompany.id, buildPayload());
                setSuccessMessage(t('companyUpdated'));
            } else {
                await createSuperAdminCompany(buildPayload());
                setSuccessMessage(t('companyAdded'));
            }

            closeForm();
            await fetchCompanies();
        } catch (err: any) {
            setError(err.message || t('saveCompanyError'));
        } finally {
            setSaving(false);
        }
    };

    const openDeleteDialog = (company: Company) => {
        setCompanyToDelete(company);
        setDeleteConfirmation("");
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        if (deleting) return;
        setCompanyToDelete(null);
        setDeleteConfirmation("");
        setDeleteDialogOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!companyToDelete?.id) return;

        const hasTests = Number(companyToDelete.testsUsed) > 0;
        const confirmationMatches = deleteConfirmation.trim() === companyToDelete.name;

        if (hasTests && !confirmationMatches) {
            setError(t('typeExactCompanyName'));
            return;
        }

        try {
            setDeleting(true);
            setError("");

            await deleteSuperAdminCompany(companyToDelete.id, hasTests);
            setSuccessMessage(
                hasTests
                    ? t('companyRemovedWithHistory')
                    : t('companyRemoved')
            );
            closeDeleteDialog();
            await fetchCompanies();
        } catch (err: any) {
            if (err.status === 409) {
                setCompanyToDelete({
                    ...companyToDelete,
                    testsUsed: err.testsUsed
                });
                setError(err.message || t('explicitConfirmationRequired'));
                return;
            }

            setError(err.message || t('removeCompanyError'));
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (date?: string) => {
        if (!date) return t('notAvailable');
        return new Date(date).toLocaleDateString(language === 'en' ? 'en-US' : 'pt-BR');
    };

    const formatTotalLicenses = (company: Company) => {
        return company.isUnlimited ? t('unlimited') : Number(company.licenses) || 0;
    };

    const formatRemainingLicenses = (company: Company) => {
        return company.isUnlimited ? t('unlimited') : Number(company.licensesRemaining) || 0;
    };

    const hasTestsToDelete = Number(companyToDelete?.testsUsed) > 0;
    const canConfirmDelete = !hasTestsToDelete || deleteConfirmation.trim() === companyToDelete?.name;

    return (
        <div>
            <MenuTop />
            <div className="companies-dashboard">
                <AdminCrudHeader
                    title={t('companies')}
                    subtitle={`${companies.length} ${t('companiesRegistered')}`}
                    addLabel={t('addCompany')}
                    addIcon={<AddBusinessIcon />}
                    onAdd={openAddForm}
                    breadcrumbItems={[
                        { label: menuT('home'), to: '/home' },
                        { label: t('causalePanel'), to: '/admin-painel-causale' },
                        { label: t('companies') }
                    ]}
                />

                <div className="companies-summary">
                    <div className="companies-summary-item">
                        <span>{t('activePlural')}</span>
                        <strong>{totals.active}</strong>
                    </div>
                    <div className="companies-summary-item">
                        <span>{t('licenses')}</span>
                        <strong>{totals.licenses}</strong>
                    </div>
                    <div className="companies-summary-item">
                        <span>{t('usedShort')}</span>
                        <strong>{totals.used}</strong>
                    </div>
                </div>

                <AdminCrudSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder={t('searchCompanies')}
                />

                <AdminCrudAlerts
                    error={error}
                    success={successMessage}
                    onClearError={() => setError("")}
                    onClearSuccess={() => setSuccessMessage("")}
                />

                {loading ? (
                    <div className="companies-loading">
                        <ProjectLoader label={t('loadingCompanies')} compact />
                    </div>
                ) : (
                    <TableContainer component={Paper} className="companies-table-container">
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('company')}</TableCell>
                                    <TableCell>CNPJ</TableCell>
                                    <TableCell>{t('status')}</TableCell>
                                    <TableCell>{t('licenses')}</TableCell>
                                    <TableCell>{t('usedShort')}</TableCell>
                                    <TableCell>{t('remaining')}</TableCell>
                                    <TableCell>{t('language')}</TableCell>
                                    <TableCell>{t('createdIn')}</TableCell>
                                    <TableCell align="center">{t('actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCompanies.map((company) => (
                                    <TableRow key={company.id} hover>
                                        <TableCell>
                                            <div className="companies-name-cell">
                                                <BusinessIcon />
                                                <div>
                                                    <strong>{company.name}</strong>
                                                    <span>{company.description || t('noDescription')}</span>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>{company.cnpj || t('notAvailable')}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={company.isPlanActive ? t('active') : t('inactive')}
                                                color={company.isPlanActive ? "success" : "default"}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{formatTotalLicenses(company)}</TableCell>
                                        <TableCell>{Number(company.testsUsed) || 0}</TableCell>
                                        <TableCell>{formatRemainingLicenses(company)}</TableCell>
                                        <TableCell>{company.activeLanguage?.name || t('notAvailable')}</TableCell>
                                        <TableCell>{formatDate(company.created_at || company.createdAt)}</TableCell>
                                        <TableCell align="center">
                                            <IconButton aria-label={t('editCompany')} onClick={() => openEditForm(company)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton aria-label={t('removeCompany')} color="error" onClick={() => openDeleteDialog(company)}>
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {filteredCompanies.length === 0 && (
                            <div className="companies-empty">{t('noCompaniesFound')}</div>
                        )}
                    </TableContainer>
                )}
            </div>

            <AdminCrudFormDialog
                open={formOpen}
                title={editingCompany ? t('editCompanyTitle') : t('addCompanyTitle')}
                closeLabel={t('close')}
                cancelLabel={t('cancel')}
                saveLabel={saving ? t('savingCompany') : t('save')}
                saving={saving}
                onClose={closeForm}
                onSave={handleSubmit}
            >
                    <TextField
                        label={t('name')}
                        value={form.name}
                        onChange={(event) => updateForm("name", event.target.value)}
                        fullWidth
                        required
                    />
                    <TextField
                        label="CNPJ"
                        value={form.cnpj}
                        onChange={(event) => updateForm("cnpj", event.target.value)}
                        fullWidth
                    />
                    <TextField
                        label={t('description')}
                        value={form.description}
                        onChange={(event) => updateForm("description", event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                    />
                    <TextField
                        label={t('licenses')}
                        value={form.licenses}
                        onChange={(event) => updateForm("licenses", event.target.value)}
                        type="number"
                        fullWidth
                        inputProps={{ min: 0, step: 1 }}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="company-language-label">{t('language')}</InputLabel>
                        <Select
                            labelId="company-language-label"
                            label={t('language')}
                            value={form.languageId}
                            onChange={(event) => updateForm("languageId", event.target.value)}
                        >
                            <MenuItem value="">{t('noLanguage')}</MenuItem>
                            {languages.map((language) => (
                                <MenuItem key={language.id} value={language.id}>
                                    {language.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <div className="companies-switch-row">
                        <div>
                            <strong>{t('activePlan')}</strong>
                            <span>{form.isPlanActive ? t('active') : t('inactive')}</span>
                        </div>
                        <Switch
                            checked={form.isPlanActive}
                            onChange={(event) => updateForm("isPlanActive", event.target.checked)}
                        />
                    </div>
            </AdminCrudFormDialog>

            <AdminCrudDeleteDialog
                open={deleteDialogOpen}
                title={t('removeCompanyTitle')}
                message={companyToDelete
                    ? hasTestsToDelete
                        ? t('companyHasTests').replace('{name}', companyToDelete.name || '').replace('{count}', String(companyToDelete.testsUsed || 0))
                        : t('confirmRemoveCompany').replace('{name}', companyToDelete.name || '')
                    : ""}
                warning={hasTestsToDelete ? t('companyTestsPreservedWarning') : undefined}
                cancelLabel={t('cancel')}
                confirmLabel={deleting ? t('removing') : t('remove')}
                deleting={deleting}
                disabled={!canConfirmDelete}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
            >
                {hasTestsToDelete && (
                    <TextField
                        label={t('typeCompanyName')}
                        value={deleteConfirmation}
                        onChange={(event) => setDeleteConfirmation(event.target.value)}
                        fullWidth
                    />
                )}
            </AdminCrudDeleteDialog>
        </div>
    );
};
